const handlePDFDownload = async (
  setSnackbarData,
  theme,
  isMobile,
  calcType
) => {
  // utility: inject print-safe CSS (and return cleanup)
  const injectPrintCSS = () => {
    const style = document.createElement("style");
    style.setAttribute("data-pdf-print-css", "true");
    style.textContent = `
      @media print { * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      [data-pdf-block] { break-inside: avoid; page-break-inside: avoid; }
      * { animation: none !important; transition: none !important; }
      [style*="position: sticky"], .MuiAppBar-root { position: static !important; top: auto !important; }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  };

  // utility: slice a tall canvas into page-sized images
  const addCanvasAsPagedImages = ({
    doc,
    canvas,
    margin,
    contentWidth,
    pageHeight,
    addHeaderToPage,
  }) => {
    const imgWidth = contentWidth;
    const scale = imgWidth / canvas.width; // mm per px vertically matches horizontally
    const usableHeightMm = pageHeight - 25 - 12; // header ~25mm, footer ~12mm
    const pageHeightPx = Math.floor(usableHeightMm / scale);

    // Build smart cut positions to avoid splitting form controls/blocks
    const container = document.getElementById("calculator-to-print");
    const domHeight = container ? container.scrollHeight : null;
    const domToCanvas = domHeight ? canvas.height / domHeight : null;
    let safeCuts = [];
    try {
      if (container && domToCanvas) {
        const candidates = container.querySelectorAll(
          "[data-pdf-block], .MuiFormControl-root, .MuiCollapse-root"
        );
        safeCuts = Array.from(candidates)
          .map((el) => {
            const top = el.offsetTop;
            const h = el.offsetHeight;
            return Math.round((top + h) * domToCanvas);
          })
          .filter((v) => v > 0 && v < canvas.height)
          .sort((a, b) => a - b);
      }
    } catch (_) {
      safeCuts = [];
    }

    let y = 0;
    let pageIndex = 0;
    while (y < canvas.height) {
      // Prefer cutting at nearest safe boundary within this page space
      const target = y + pageHeightPx;
      const MIN_MARGIN = 40; // px on canvas to avoid tiny slivers at page bottoms
      const MAX_GAP = Math.round(pageHeightPx * 0.25); // don't leave >25% empty
      let cutAt = target;
      if (safeCuts.length) {
        const within = safeCuts.filter(
          (v) => v > y + MIN_MARGIN && v <= target
        );
        if (within.length) {
          const candidate = within[within.length - 1];
          // Only use candidate if it doesn't leave a large empty gap
          if (target - candidate <= MAX_GAP) {
            cutAt = candidate;
          }
        }
      }

      const sliceHeight = Math.min(cutAt - y, canvas.height - y);
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeight;
      const ctx = pageCanvas.getContext("2d");
      ctx.drawImage(
        canvas,
        0,
        y,
        canvas.width,
        sliceHeight,
        0,
        0,
        canvas.width,
        sliceHeight
      );

      if (pageIndex > 0) {
        doc.addPage();
      }
      addHeaderToPage();

      const imgHeightMm = sliceHeight * scale;
      doc.addImage(pageCanvas, "PNG", margin, 25, imgWidth, imgHeightMm);

      y += sliceHeight;
      pageIndex += 1;
    }
  };

  let progressContainer;
  try {
    // Create progress indicator
    progressContainer = document.createElement("div");
    Object.assign(progressContainer.style, {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      backgroundColor: theme.palette.background.paper,
      padding: "20px 30px",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "15px",
    });

    const progressText = document.createElement("span");
    progressText.style.color = theme.palette.text.primary;
    progressText.style.fontSize = "16px";
    progressText.style.fontWeight = "500";
    progressText.textContent = "Generating PDF... 0%";

    const progressBar = document.createElement("div");
    Object.assign(progressBar.style, {
      width: "200px",
      height: "6px",
      backgroundColor: theme.palette.grey[200],
      borderRadius: "3px",
    });

    const progressFill = document.createElement("div");
    Object.assign(progressFill.style, {
      width: "0%",
      height: "100%",
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
      transition: "width 0.3s",
    });

    progressBar.appendChild(progressFill);
    progressContainer.appendChild(progressText);
    progressContainer.appendChild(progressBar);
    document.body.appendChild(progressContainer);

    const updateProgress = (percent) => {
      progressText.textContent = `Generating PDF... ${percent}%`;
      progressFill.style.width = `${percent}%`;
    };

    // Import libraries
    const jsPDFModule = await import("jspdf");
    const html2canvasModule = await import("html2canvas");
    const jsPDF = jsPDFModule.default;
    const html2canvas = html2canvasModule.default;

    // Capture container
    const container = document.getElementById("calculator-to-print");
    if (!container) throw new Error("Calculator content not found");

    const cleanupPrintCSS = injectPrintCSS();

    // Let fonts/layout settle
    await Promise.all([
      new Promise((resolve) => setTimeout(resolve, 400)),
      document.fonts && document.fonts.ready
        ? document.fonts.ready
        : Promise.resolve(),
    ]);

    updateProgress(20);

    // Normalize capture width to the PDF content width to avoid oversized UI
    const pdfWidthPx = Math.floor((contentWidth || 190) * 3.78); // mm -> px
    const originalContainerStyles = {
      width: container.style.width,
      maxWidth: container.style.maxWidth,
      overflow: container.style.overflow,
    };
    container.style.width = `${pdfWidthPx}px`;
    container.style.maxWidth = `${pdfWidthPx}px`;
    container.style.overflow = "visible";

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      width: pdfWidthPx,
      windowWidth: pdfWidthPx,
      ignoreElements: (el) => el.classList?.contains("no-print-element"),
    });

    // Restore container styles
    Object.assign(container.style, originalContainerStyles);

    updateProgress(40);

    // Init PDF
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;

    const addHeaderToPage = () => {
      const primaryColor = theme.palette.primary.main;
      const r = parseInt(primaryColor.slice(1, 3), 16);
      const g = parseInt(primaryColor.slice(3, 5), 16);
      const b = parseInt(primaryColor.slice(5, 7), 16);
      doc.setFillColor(r, g, b);
      doc.rect(0, 0, pageWidth, 20, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("Roboto", "normal");
      doc.setFontSize(20);
      doc.text("OfferMarket", margin, 14);
      doc.text(`${calcType} calculator`, margin + 55, 14);
      doc.setTextColor(0, 0, 0);
      doc.setFont("Helvetica", "normal");
      return 25;
    };

    const addFooterToPage = (pageNum, totalPages) => {
      const dateStr = new Date().toLocaleDateString();
      doc.setFont("Roboto", "normal");
      doc.setFontSize(11);
      const primaryColor = theme.palette.primary.main;
      const r = parseInt(primaryColor.slice(1, 3), 16);
      const g = parseInt(primaryColor.slice(3, 5), 16);
      const b = parseInt(primaryColor.slice(5, 7), 16);
      doc.setTextColor(r, g, b);
      doc.text(
        `Generated on ${dateStr} via OfferMarket.us | Page ${pageNum} of ${totalPages}`,
        margin,
        pageHeight - 12
      );
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(0, 0, 0);
    };

    // Slice tall canvas into pages
    addCanvasAsPagedImages({
      doc,
      canvas,
      margin,
      contentWidth,
      pageHeight,
      addHeaderToPage,
    });

    updateProgress(85);

    // Add footer to each page
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooterToPage(i, totalPages);
    }

    updateProgress(95);
    doc.save(`${calcType} calculator results.pdf`);

    // Cleanup
    cleanupPrintCSS();
    document.body.removeChild(progressContainer);

    setSnackbarData({
      message: "PDF generated successfully!",
      options: {
        variant: "success",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      },
    });
  } catch (error) {
    if (progressContainer && document.body.contains(progressContainer)) {
      document.body.removeChild(progressContainer);
    }
    console.error("Error generating PDF:", error);
    setSnackbarData({
      message: "Error generating PDF. Please try again.",
      options: {
        variant: "error",
        autoHideDuration: 5000,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      },
    });
  }
};

export default handlePDFDownload;
